import _cloneDeep from 'lodash/cloneDeep'
import _isEqual from 'lodash/isEqual'
import Image from 'next/image'
import { memo, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

import { SCREEN_SIZE, SPACE } from '@nxt-insurance/ui-kit/constants'
import { bgColor } from '@nxt-insurance/ui-kit/styles/colors'
import { paddingX, paddingY } from '@nxt-insurance/ui-kit/styles/padding'
import type { Color, Space } from '@nxt-insurance/ui-kit/types'

import { generateSrcSet, urlFor } from '@/cms/client'
import type { Section } from '@/cms/sections'
import { BlockContent } from '@/components/renderers/BlockContent'
import { FlexibleImage } from '@/components/renderers/FlexibleImage'
import { MAX_CONTENT_WIDTH } from '@/constants/content'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { justifyAlignmentTailwind } from '@/utils/style'

export type MultiColumnSectionT = Exclude<
  Extract<Section, { name: string | null }>,
  { _type: 'website.richContent.section' }
>

export const multiColumnSection = tv({
  slots: {
    container: ['flex flex-col md:flex-row'],
    anchorWrapper: '-mt-[56px] w-full overflow-x-hidden pt-[56px] lg:-mt-[72px] lg:pt-[72px]',
    columnContainer: 'relative overflow-hidden',
    section: 'flex-grow gap-x-6 px-6 py-12 md:px-12 lg:px-[80px] lg:py-[80px]',
  },
})

const getColumnPaddingValues = (index: number, sideSpacing: number, itemsCount: number) => {
  let paddingRightValue, paddingLeftValue
  /*
      We want padding for multi-columns to be added according to the index of the column.
        First column should have padding only on left side.
        Last column should have padding only on right side.
        Middle column(s) should have half the padding on either sides.
    */
  switch (index) {
    case 0:
      paddingLeftValue = sideSpacing
      break
    case itemsCount - 1:
      paddingRightValue = sideSpacing
      break
    default:
      paddingLeftValue = sideSpacing / 2
      paddingRightValue = sideSpacing / 2
      break
  }

  // For single column (first === last), we want to add padding on both sides.
  if (itemsCount - 1 === 0) {
    paddingLeftValue = sideSpacing
    paddingRightValue = sideSpacing
  }

  return {
    paddingLeftValue,
    paddingRightValue,
  }
}

const Column = memo(
  (props: {
    hasPriority: boolean
    column: MultiColumnSectionT['columns'][number]
    oneColumnHasFullSizeImage: boolean
    contentWidth: number
    columnWidth: number
    index: number
    backgroundColor?: Color
    contentColor?: Color
    sectionName: string
    nextColumnHasSameBgColor: boolean
    verticalPadding?: Space
    horizontalPadding?: Space
    itemsCount: number
    reverseOrderForWideScreen: boolean
    widths: number[]
    sideSpacing: number
  }) => {
    const {
      column,
      contentWidth,
      columnWidth,
      oneColumnHasFullSizeImage,
      index,
      nextColumnHasSameBgColor,
    } = props
    const backgroundImageProps = useMemo(() => {
      if (!column.backgroundImage?.asset) {
        return
      }
      const url = urlFor(column.backgroundImage).width(contentWidth * 2)
      return {
        src: url.url(),
        srcSet: generateSrcSet(url),
        loader: ({ src }: { src: string }) => src,
      }
    }, [column.backgroundImage, contentWidth])

    const { columnContainer, section } = multiColumnSection()

    const { paddingLeftValue, paddingRightValue } = getColumnPaddingValues(
      index,
      props.sideSpacing,
      props.itemsCount
    )

    const columnContainerProps = {
      className: columnContainer({
        className: bgColor({
          color: props.backgroundColor ?? column.backgroundColor ?? undefined,
        }),
      }),
      style: {
        width: props.widths[index],
        paddingLeft: paddingLeftValue,
        paddingRight: paddingRightValue,
      },
      key: column._key,
    }

    if (column.fullSizeImage && column.fullSizeImageAlignment === 'full') {
      return (
        <div {...columnContainerProps}>
          <FlexibleImage
            node={{
              index,
              isInline: false,
              renderNode: () => null,
              value: column.fullSizeImage,
            }}
            containerWidth={columnWidth}
            slideToEdge={column.fullSizeImageSlideToEdge ?? false}
            itemsCount={props.itemsCount}
            hasPriority={props.hasPriority}
          />
        </div>
      )
    }
    // if the following column hase the same bg color as the current one remove the bottom
    // padding on mobile to make the vertical distance between collapsed columns not too big
    // Don't remove the padding if one of the columns has a fullWidthImage.
    const noPaddingBottom = nextColumnHasSameBgColor && !oneColumnHasFullSizeImage

    return (
      <div {...columnContainerProps}>
        {backgroundImageProps ? (
          <Image
            {...backgroundImageProps}
            loading={props.hasPriority ? 'eager' : 'lazy'}
            height={undefined}
            width={undefined}
            fill
            alt=""
            style={{ objectFit: column.backgroundImage?.fit ?? undefined }}
          />
        ) : null}
        {column.fullSizeImage && column.fullSizeImageAlignment === 'top' ? (
          <FlexibleImage
            hasPriority={props.hasPriority}
            node={{
              index,
              isInline: false,
              renderNode: () => null,
              value: column.fullSizeImage,
            }}
            containerWidth={columnWidth}
            slideToEdge={column.fullSizeImageSlideToEdge ?? false}
            itemsCount={props.itemsCount}
          />
        ) : null}
        <section
          className={twMerge(
            section(),
            props.verticalPadding &&
              paddingY({
                space: {
                  initial: props.verticalPadding,
                  md: props.verticalPadding,
                  lg: props.verticalPadding,
                },
              }),
            props.horizontalPadding &&
              paddingX({
                space: {
                  initial: props.horizontalPadding,
                  md: props.horizontalPadding,
                  lg: props.horizontalPadding,
                },
              }),
            noPaddingBottom && 'pb-0 md:pb-0 lg:pb-0',
            justifyAlignmentTailwind[column.alignVertically || 'top']
          )}
        >
          <BlockContent
            blocks={column.content ?? []}
            align={column.alignHorizontally ?? 'left'}
            sectionName={props.sectionName}
            width={contentWidth}
            contentColor={props.contentColor}
            backgroundColor={props.backgroundColor}
            space={column.space ?? undefined}
            reverseOrderForWideScreen={props.reverseOrderForWideScreen}
            hasPriority={props.hasPriority}
          />
        </section>
        {column.fullSizeImage && column.fullSizeImageAlignment === 'bottom' ? (
          <FlexibleImage
            node={{
              index: index,
              isInline: false,
              renderNode: () => null,
              value: column.fullSizeImage,
            }}
            itemsCount={props.itemsCount}
            containerWidth={columnWidth}
            slideToEdge={column.fullSizeImageSlideToEdge ?? false}
            hasPriority={props.hasPriority}
          />
        ) : null}
      </div>
    )
  },
  _isEqual
)

export const MultiColumnSection = memo(
  (props: {
    section: MultiColumnSectionT
    backgroundColor?: Color
    contentColor?: Color
    verticalPadding?: Space
    horizontalPadding?: Space
    index: number
  }) => {
    const windowWidth = useWindowWidth()

    const columns = useMemo(() => {
      if (!props.section.columns || props.section.columns.length === 0) {
        return []
      }
      if (windowWidth <= SCREEN_SIZE.M || !props.section.reverseColumnOrderForWideScreen) {
        return props.section.columns
      }
      const columnsCopy = _cloneDeep(props.section.columns)
      return columnsCopy.reverse()
    }, [props.section.columns, props.section.reverseColumnOrderForWideScreen, windowWidth])

    const sideSpacing = useMemo(() => {
      return (windowWidth - MAX_CONTENT_WIDTH - SPACE.small) / 2
    }, [windowWidth])

    const widths = useMemo(() => {
      if (windowWidth <= SCREEN_SIZE.M) {
        return columns.map(() => windowWidth)
      }
      return columns.map(() => (1 / columns.length) * windowWidth)
    }, [columns, windowWidth])

    if (columns.length === 0) {
      return null
    }

    const { name } = props.section

    const oneColumnHasFullSizeImage = columns.some((col) => Boolean(col.fullSizeImage))
    const columnWidth = Math.round(MAX_CONTENT_WIDTH / columns.length)
    const contentWidth = columnWidth - SPACE.xxlarge * 2

    const { anchorWrapper, container } = multiColumnSection()

    return (
      <div className={anchorWrapper()} id={name ?? ''}>
        <div className={container()}>
          {columns.map((column, i) => (
            <Column
              hasPriority={props.index < 2}
              key={column._key}
              column={column}
              index={i}
              columnWidth={columnWidth}
              contentWidth={contentWidth}
              oneColumnHasFullSizeImage={oneColumnHasFullSizeImage}
              backgroundColor={props.backgroundColor}
              contentColor={props.contentColor}
              sectionName={props.section.name ?? ''}
              verticalPadding={props.section.verticalPadding ?? props.verticalPadding}
              horizontalPadding={props.horizontalPadding}
              reverseOrderForWideScreen={props.section.reverseColumnOrderForWideScreen ?? false}
              itemsCount={columns.length}
              nextColumnHasSameBgColor={column.backgroundColor === columns[i + 1]?.backgroundColor}
              widths={widths}
              sideSpacing={sideSpacing}
            />
          ))}
        </div>
      </div>
    )
  },
  _isEqual
)
